import { Component } from '@theme/component';

/**
 * NewsletterModal component for modal-driven lead capture with AE external CRM integration.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog element
 * @property {HTMLFormElement} form - The external lead form
 * @property {HTMLButtonElement} closeBtn - The close button
 * @property {HTMLElement} fields - Container for form fields
 * @property {HTMLElement} successContainer - Success state container (within modal)
 * @property {HTMLElement} errorContainer - Error state container (within modal)
 * @property {HTMLInputElement[]} inp1 - Name input (inp_1)
 * @property {HTMLInputElement[]} inp2 - Lastname input (inp_2)
 * @property {HTMLInputElement[]} inp3 - Email input (inp_3)
 * @property {HTMLInputElement[]} inp4899 - Document input
 * @property {HTMLInputElement[]} inp37 - Phone input
 * @property {HTMLInputElement[]} optin - Optin checkbox
 * @property {HTMLButtonElement} submitBtn - Submit button
 * @property {HTMLButtonElement} retryBtn - Retry button
 *
 * @extends Component<Refs>
 */
export default class NewsletterModal extends Component {
  #triggerButton = null;
  #abortController = null;
  #submitTimeoutId = null;
  #closeTimeoutId = null;
  #autoOpenTimeoutId = null;
  #wasAutoOpened = false;
  #boundHandleSubmit = null;
  #boundHandleRetry = null;
  #boundHandleDialogClick = null;
  #boundHandleKeyDown = null;

  #getFocusableElements() {
    const { dialog } = this.refs;
    if (!dialog) return [];

    return [...dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (element) => !element.hasAttribute('disabled') && !element.hasAttribute('hidden')
    );
  }

  /** @type {string[]} */
  requiredRefs = ['dialog', 'form', 'closeBtn', 'fields'];

  connectedCallback() {
    super.connectedCallback();

    // Store trigger button reference for focus return
    this.#triggerButton = this.closest('.newsletter')?.querySelector('.newsletter__button');

    // Bind handlers
    this.#boundHandleSubmit = this.#handleSubmit.bind(this);
    this.#boundHandleRetry = this.#handleRetry.bind(this);
    this.#boundHandleDialogClick = this.#handleDialogClick.bind(this);
    this.#boundHandleKeyDown = this.#handleKeyDown.bind(this);

    // Pre-fill from URL query params
    this.#prefillFromURL();

    // Attach form submit handler
    this.refs.form?.addEventListener('submit', this.#boundHandleSubmit);

    // Attach retry button handler
    this.refs.retryBtn?.addEventListener('click', this.#boundHandleRetry);

    // Auto-open on first visit (no cookie)
    this.#autoOpenTimeoutId = window.setTimeout(() => {
      this.#autoOpen();
      this.#autoOpenTimeoutId = null;
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#closeTimeoutId) {
      clearTimeout(this.#closeTimeoutId);
      this.#closeTimeoutId = null;
    }
    if (this.#submitTimeoutId) {
      clearTimeout(this.#submitTimeoutId);
      this.#submitTimeoutId = null;
    }
    if (this.#autoOpenTimeoutId) {
      clearTimeout(this.#autoOpenTimeoutId);
      this.#autoOpenTimeoutId = null;
    }
    this.refs.form?.removeEventListener('submit', this.#boundHandleSubmit);
    this.refs.retryBtn?.removeEventListener('click', this.#boundHandleRetry);
    this.refs.dialog?.removeEventListener('click', this.#boundHandleDialogClick);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);
  }

  /**
   * Opens the modal automatically if the user has not yet accepted the
   * site cookie consent (avada_cookie_consent cookie absent).
   */
  #autoOpen() {
    const hasConsent = document.cookie.split(';').some((c) => c.trim().startsWith('avada_cookie_consent='));
    if (hasConsent) return;
    this.#wasAutoOpened = true;
    this.openModal();
  }

  /**
   * Opens the modal dialog.
   */
  openModal() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    // Reset form state on open
    this.#resetFormState();

    // Show modal
    dialog.showModal();
    dialog.classList.remove('newsletter-modal__dialog--closing');
    dialog.classList.add('newsletter-modal__dialog--opening');

    // Attach close handlers after modal is open
    dialog.addEventListener('click', this.#boundHandleDialogClick);
    this.addEventListener('keydown', this.#boundHandleKeyDown);

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.classList.remove('newsletter-modal__dialog--opening');
      this.#closeTimeoutId = null;
    }, 140);

    // Focus first input
    requestAnimationFrame(() => {
      const firstInput = this.refs.form?.querySelector('input:not([type="hidden"])');
      if (firstInput) {
        firstInput.focus();
      }
    });
  }

  /**
   * Closes the modal dialog.
   */
  closeModal() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

     if (dialog.classList.contains('newsletter-modal__dialog--closing')) return;

    // Remove event listeners
    dialog.removeEventListener('click', this.#boundHandleDialogClick);
    this.removeEventListener('keydown', this.#boundHandleKeyDown);

    dialog.classList.remove('newsletter-modal__dialog--opening');
    dialog.classList.add('newsletter-modal__dialog--closing');

    this.#closeTimeoutId = window.setTimeout(() => {
      dialog.close();
      dialog.classList.remove('newsletter-modal__dialog--closing');
      this.#closeTimeoutId = null;
    }, 140);

    // Return focus to trigger button only if user opened the modal manually
    if (!this.#wasAutoOpened && this.#triggerButton) {
      window.setTimeout(() => {
        this.#triggerButton.focus();
      }, 150);
    }
    this.#wasAutoOpened = false;
  }

  /**
   * Handles dialog click events (backdrop click detection).
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    const { dialog } = this.refs;
    if (!dialog || event.target !== dialog) return;

    // Get modal surface dimensions
    const surface = dialog.querySelector('.newsletter-modal__surface');
    if (!surface || !(event instanceof MouseEvent)) {
      this.closeModal();
      return;
    }

    const { left, right, top, bottom } = surface.getBoundingClientRect();
    const clickedBackdrop =
      event.clientX < left ||
      event.clientX > right ||
      event.clientY < top ||
      event.clientY > bottom;

    if (clickedBackdrop) {
      this.closeModal();
    }
  }

  /**
   * Handles keydown events (ESC to close).
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Tab') {
      const focusableElements = this.#getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
      return;
    }

    if (event.key !== 'Escape') return;

    // Native dialog ESC behavior - we just need to clean up our state
    // The browser handles the actual close
    event.preventDefault();
    this.closeModal();
  }

  /**
   * Pre-fill form fields from URL query parameters.
   */
  #prefillFromURL() {
    const params = new URLSearchParams(window.location.search);
    const prefillFields = ['inp_1', 'inp_2', 'inp_3'];

    for (const name of prefillFields) {
      if (params.has(name)) {
        const input = this.refs.form?.querySelector(`[name="${name}"]`);
        if (input) {
          input.value = decodeURIComponent(params.get(name) || '');
        }
      }
    }
  }

  /**
   * Resets form state (clear validation, hide states).
   */
  #resetFormState() {
    // Clear validation states
    const invalidInputs = this.refs.form?.querySelectorAll('[aria-invalid="true"]');
    if (invalidInputs) {
      for (const input of invalidInputs) {
        input.removeAttribute('aria-invalid');
      }
    }

    // Hide error/success containers
    if (this.refs.errorContainer) {
      this.refs.errorContainer.setAttribute('aria-hidden', 'true');
    }
    if (this.refs.successContainer) {
      this.refs.successContainer.setAttribute('aria-hidden', 'true');
    }
    this.querySelector('[ref="imageColumn"]')?.classList.remove('newsletter-modal__image--hidden-on-success');
    this.classList.remove('newsletter-modal--success-image-only');

    // Show fields container
    if (this.refs.fields) {
      this.refs.fields.style.display = '';
    }

    // Reset submit button state
    if (this.refs.submitBtn) {
      this.refs.submitBtn.disabled = false;
      this.refs.submitBtn.textContent = this.refs.submitBtn.dataset.originalText || 'Registrarme';
    }
  }

  /**
   * Handles form submission.
   * @param {Event} event
   */
  #handleSubmit(event) {
    event.preventDefault();

    // Clear previous validation states
    this.#clearValidationStates();

    // Run validation
    const isValid = this.#validateForm();
    if (!isValid) return;

    // Submit to external endpoint
    this.#submitToExternalEndpoint();
  }

  /**
   * Clears all validation states from inputs.
   */
  #clearValidationStates() {
    const inputs = this.refs.form?.querySelectorAll('input');
    if (inputs) {
      for (const input of inputs) {
        input.removeAttribute('aria-invalid');
      }
    }
  }

  /**
   * Validates form fields. Returns true if valid.
   * Marks invalid fields with aria-invalid="true".
   * @returns {boolean}
   */
  #validateForm() {
    let isValid = true;
    const invalidFields = [];

    // inp_1: Nombre (min 2 chars)
    const inp1 = this.refs.form?.querySelector('[name="inp_1"]');
    if (inp1 && inp1.value.trim().length < 2) {
      inp1.setAttribute('aria-invalid', 'true');
      invalidFields.push(inp1);
      isValid = false;
    }

    // inp_2: Apellidos (min 2 chars)
    const inp2 = this.refs.form?.querySelector('[name="inp_2"]');
    if (inp2 && inp2.value.trim().length < 2) {
      inp2.setAttribute('aria-invalid', 'true');
      invalidFields.push(inp2);
      isValid = false;
    }

    // inp_3: Email (valid format)
    const inp3 = this.refs.form?.querySelector('[name="inp_3"]');
    if (inp3 && !this.#isValidEmail(inp3.value)) {
      inp3.setAttribute('aria-invalid', 'true');
      invalidFields.push(inp3);
      isValid = false;
    }

    // inp_4899: Documento (exactly 13 digits)
    const inp4899 = this.refs.form?.querySelector('[name="inp_4899"]');
    if (inp4899 && !/^[0-9]{13}$/.test(inp4899.value)) {
      inp4899.setAttribute('aria-invalid', 'true');
      invalidFields.push(inp4899);
      isValid = false;
    }

    // inp_4[]: DOB day/month/year required and in range
    const dobDay = this.refs.form?.querySelector('[data-dob-part="day"]');
    const dobMonth = this.refs.form?.querySelector('[data-dob-part="month"]');
    const dobYear = this.refs.form?.querySelector('[data-dob-part="year"]');
    const dayValue = Number.parseInt(dobDay?.value || '', 10);
    const monthValue = Number.parseInt(dobMonth?.value || '', 10);
    const yearValue = Number.parseInt(dobYear?.value || '', 10);
    const currentYear = new Date().getFullYear();

    if (!dobDay || Number.isNaN(dayValue) || dayValue < 1 || dayValue > 31) {
      dobDay?.setAttribute('aria-invalid', 'true');
      if (dobDay) invalidFields.push(dobDay);
      isValid = false;
    }

    if (!dobMonth || Number.isNaN(monthValue) || monthValue < 1 || monthValue > 12) {
      dobMonth?.setAttribute('aria-invalid', 'true');
      if (dobMonth) invalidFields.push(dobMonth);
      isValid = false;
    }

    if (!dobYear || Number.isNaN(yearValue) || yearValue < 1900 || yearValue > currentYear) {
      dobYear?.setAttribute('aria-invalid', 'true');
      if (dobYear) invalidFields.push(dobYear);
      isValid = false;
    }

    // inp_37: Celular (exactly 8 digits)
    const inp37 = this.refs.form?.querySelector('[name="inp_37"]');
    if (inp37 && !/^[0-9]{8}$/.test(inp37.value)) {
      inp37.setAttribute('aria-invalid', 'true');
      invalidFields.push(inp37);
      isValid = false;
    }

    // optin: checkbox must be checked
    const optin = this.refs.form?.querySelector('[name="optin"]');
    if (optin && !optin.checked) {
      optin.setAttribute('aria-invalid', 'true');
      invalidFields.push(optin);
      isValid = false;
    }

    // Focus first invalid field
    if (invalidFields.length > 0) {
      invalidFields[0].focus();
    }

    return isValid;
  }

  /**
   * Validates email format.
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return email.includes('@') && email.includes('.') && email.length >= 5;
  }

  /**
   * Submits form to external endpoint via GET.
   */
  async #submitToExternalEndpoint() {
    const { form, submitBtn, fields, successContainer, errorContainer } = this.refs;
    if (!form) return;

    // Build URL with explicit ordering/control so legacy CRM receives the expected payload.
    const params = new URLSearchParams();
    const elements = form.querySelectorAll('input');

    for (const element of elements) {
      if (!(element instanceof HTMLInputElement)) continue;
      if (!element.name) continue;
      if (element.type === 'checkbox') continue;
      params.append(element.name, element.value);
    }

    const optin = form.querySelector('[name="optin"]');
    if (optin instanceof HTMLInputElement && optin.checked) {
      params.append('optin', 'y');
    }

    const endpoint = form.action;
    const submitUrl = `${endpoint}?${params.toString()}`;

    // Disable submit button
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.originalText = submitBtn.textContent;
      submitBtn.textContent = 'Enviando...';
    }

    // Set up timeout
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const timeoutSignal = this.#abortController.signal;

    // 10 second timeout
    this.#submitTimeoutId = setTimeout(() => {
      this.#abortController?.abort();
    }, 10000);

    try {
      // GET request with no-cors mode (opaque response)
      await fetch(submitUrl, { method: 'GET', mode: 'no-cors', signal: timeoutSignal });

      // Success (fetch completed without network error)
      clearTimeout(this.#submitTimeoutId);
      this.#submitTimeoutId = null;

      // Show success within modal
      this.#showModalSuccess();

    } catch (error) {
      clearTimeout(this.#submitTimeoutId);
      this.#submitTimeoutId = null;

      // Restore button state
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.originalText || 'Registrarme';
      }

      // Show error within modal
      this.#showModalError(error.name === 'AbortError' ? 'Tiempo de espera agotado. Intenta de nuevo.' : 'Error de conexión. Intenta de nuevo.');
    }
  }

  /**
   * Shows success state within modal.
   */
  #showModalSuccess() {
    const { fields, successContainer } = this.refs;
    const imageColumn = this.querySelector('[ref="imageColumn"]');

    // Hide fields
    if (fields) {
      fields.style.display = 'none';
    }

    // Show success container
    if (successContainer) {
      successContainer.removeAttribute('aria-hidden');
      if (successContainer.classList.contains('newsletter-modal__success--text-only')) {
        imageColumn?.classList.add('newsletter-modal__image--hidden-on-success');
      }
      if (successContainer.classList.contains('newsletter-modal__success--with-image')) {
        this.classList.add('newsletter-modal--success-image-only');
        imageColumn?.classList.add('newsletter-modal__image--hidden-on-success');
      }
    }

    if (successContainer) {
      successContainer.focus?.();
    }
  }

  /**
   * Shows error state within modal.
   * @param {string} message
   */
  #showModalError(message) {
    const { errorContainer, fields } = this.refs;

    // Update error message
    const errorMessage = errorContainer?.querySelector('[ref="errorMessage"]');
    if (errorMessage) {
      errorMessage.textContent = message;
    }

    // Show error container
    if (errorContainer) {
      errorContainer.removeAttribute('aria-hidden');
    }
  }

  /**
   * Handles retry button click.
   */
  #handleRetry() {
    const { errorContainer, form } = this.refs;

    // Hide error
    if (errorContainer) {
      errorContainer.setAttribute('aria-hidden', 'true');
    }

    // Focus first input
    const firstInput = form?.querySelector('input:not([type="hidden"])');
    if (firstInput) {
      firstInput.focus();
    }
  }
}

if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', NewsletterModal);
}
